import type { ScopedListRow } from '@/features/scoped-tree-viewer';

import type { CareStateDomainDb } from '../model/types';
import { actualLink } from './links';
import { CARE_STATE_SCOPE_LABEL } from './scopeLabels';

export type BuildCareStateGroupedListRowsOptions = {
  includeOrphansGroup?: boolean;
};

function stableSortBy<T>(items: ReadonlyArray<T>, key: (item: T) => string): T[] {
  return [...items].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}

export function buildCareStateGroupedListRows(
  db: CareStateDomainDb,
  options?: BuildCareStateGroupedListRowsOptions
): ScopedListRow[] {
  const includeOrphansGroup = options?.includeOrphansGroup ?? true;

  const problems = stableSortBy(db.problems, (p) => `${p.title}\n${p.id}`);
  const requests = stableSortBy(db.requests, (r) => `${r.title}\n${r.id}`);

  const problemIdSet = new Set(db.problems.map((p) => p.id));

  const requestIdsByProblemId = new Map<string, string[]>();
  const orphanRequestIds: string[] = [];

  for (const request of requests) {
    const link = actualLink(request.link, db);
    if (link.type === 'problem' && problemIdSet.has(link.id)) {
      const list = requestIdsByProblemId.get(link.id) ?? [];
      list.push(request.id);
      requestIdsByProblemId.set(link.id, list);
      continue;
    }
    if (includeOrphansGroup) orphanRequestIds.push(request.id);
  }

  const rows: ScopedListRow[] = [];

  // Root scope selector row.
  rows.push({
    id: 'patient:root',
    kind: 'patient',
    label: CARE_STATE_SCOPE_LABEL.patient,
    depth: 0,
  });

  for (const problem of problems) {
    rows.push({
      id: `problem:${problem.id}`,
      kind: 'problem',
      label: problem.title,
      depth: 1,
    });

    const requestIds = requestIdsByProblemId.get(problem.id) ?? [];
    for (const requestId of requestIds) {
      const request = db.requests.find((r) => r.id === requestId);
      if (!request) continue;
      rows.push({
        id: `request:${request.id}`,
        kind: 'request',
        label: request.title,
        depth: 2,
      });
    }
  }

  if (includeOrphansGroup) {
    for (const requestId of orphanRequestIds) {
      const request = db.requests.find((r) => r.id === requestId);
      if (!request) continue;
      rows.push({
        id: `request:${request.id}`,
        kind: 'orphanRequest',
        label: request.title,
        depth: 1,
      });
    }
  }

  return rows;
}
